'use client';

import { readOptionalBrowserValue, writeOptionalBrowserValue } from '@/lib/optional-browser-storage';

import { type CSSProperties, FormEvent, useEffect, useRef, useState } from 'react';
import { BookOpen, Bot, History, Loader2, MessageCircle, Paperclip, Phone, Send, X } from 'lucide-react';
import { isOfficeOpen } from '@/lib/support/office-hours';
import { companyInfo } from '@/config/company';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { subscribeSupportRequests } from '@/lib/support/open-request';

const TWILPER_PRESENCE_KEY = 'printen_chat_twilper_conversation';

type Role = 'assistant' | 'user';

type ProductCard = {
  id: string;
  name: string;
  href: string;
  image?: string | null;
  price?: string;
  reason?: string;
  features?: string[];
};

type ArticleLink = {
  title: string;
  summary: string;
  href: string;
};

type Message = {
  id: string;
  role: Role;
  content: string;
  products?: ProductCard[];
  articles?: ArticleLink[];
  /** Onder dit antwoord staat de knop om de vraag aan het team door te geven. */
  handoffOffer?: boolean;
};

type HistoryItem = {
  id: string;
  sessionId: string;
  preview: string;
  lastMessageAt?: string;
  lastIntent?: string;
};

type ChatState = {
  flow?: 'order_status' | 'support' | 'product_advice';
  orderNumber?: string;
  verifiedOrderId?: string;
  attempts?: number;
  sessionId?: string;
  twilperConversationId?: string;
  conversationId?: string;
  locale?: string;
};

/** Antwoord van /api/chatbot/message: JSON, of de regels van de NDJSON-stream. */
type ChatPayload = {
  type?: string;
  content?: unknown;
  message?: unknown;
  reply?: string;
  state?: ChatState;
  products?: ProductCard[];
  articles?: ArticleLink[];
  silent?: boolean;
  handoffOffer?: boolean;
};

type Locale = 'nl' | 'de' | 'en' | 'fr';

const kennisbankPad: Record<string, string> = {
  nl: '/nl/kennisbank',
  de: '/de/wissensdatenbank',
  en: '/en/knowledge-base',
  fr: '/fr/base-de-connaissances',
};

const widgetCopy: Record<Locale, {
  welcome: string;
  title: string;
  subtitle: string;
  loading: string;
  placeholder: string;
  fallback: string;
  readMore: string;
  error: string;
  open: string;
  close: string;
  send: string;
  history: string;
  historyLoaded: string;
  noHistory: string;
  historyTitle: string;
  newChat: string;
  openChat: string;
  suggestions: string[];
  kbLabel: string;
  emailTitle: string;
  emailHint: string;
  emailPlaceholder: string;
  emailContinue: string;
  emailInvalid: string;
  handoffButton: string;
}> = {
  nl: {
    welcome: 'Hallo! Ik ben de Printen.eu assistent. Ik kan orderstatus veilig opzoeken, verzoeken doorzetten naar support en helpen met producten of bestandseisen.',
    title: 'Printen.eu assistent',
    subtitle: 'Orderhulp en productadvies',
    loading: 'Bezig met controleren...',
    placeholder: 'Typ je vraag...',
    fallback: 'Sorry, ik kon geen antwoord maken. Probeer het opnieuw.',
    readMore: 'Lees er meer over',
    error: 'Sorry, er ging iets mis. Probeer het opnieuw of mail support@printen.eu.',
    open: 'Chat openen',
    close: 'Chat sluiten',
    send: 'Bericht sturen',
    history: 'Eerdere chat openen',
    historyLoaded: 'Eerdere chat geladen.',
    noHistory: 'Er is nog geen eerdere chat voor deze sessie.',
    historyTitle: 'Eerdere chats',
    newChat: 'Nieuwe chat',
    openChat: 'Openen',
    suggestions: ['Productadvies: help mij kiezen', 'Wat is de status van mijn bestelling?', 'Hebben jullie kleine stickertjes op rol?'],
    kbLabel: 'Kennisbank',
    emailTitle: 'Voordat we beginnen', emailHint: 'Vul je e-mailadres in. Zo kunnen we je gesprek terugvinden en contact opnemen als dat nodig is.', emailPlaceholder: 'jouw@email.nl', emailContinue: 'Chat starten', emailInvalid: 'Vul een geldig e-mailadres in.',
    handoffButton: 'Ja, zet mijn vraag door naar het team',
  },
  de: {
    welcome: 'Hallo! Ich bin der Printen.eu Assistent. Ich kann Bestellstatus sicher prufen, Anfragen an den Support weiterleiten und bei Produkten oder Dateivorgaben helfen.',
    title: 'Printen.eu Assistent',
    subtitle: 'Bestellhilfe und Produktempfehlung',
    loading: 'Wird gepruft...',
    placeholder: 'Ihre Frage eingeben...',
    fallback: 'Entschuldigung, ich konnte keine Antwort erstellen. Bitte versuchen Sie es erneut.',
    readMore: 'Mehr dazu lesen',
    error: 'Entschuldigung, etwas ist schiefgelaufen. Versuchen Sie es erneut oder schreiben Sie an support@printen.eu.',
    open: 'Chat offnen',
    close: 'Chat schliessen',
    send: 'Nachricht senden',
    history: 'Vorherigen Chat oeffnen',
    historyLoaded: 'Vorheriger Chat geladen.',
    noHistory: 'Fuer diese Sitzung gibt es noch keinen vorherigen Chat.',
    historyTitle: 'Vorherige Chats',
    newChat: 'Neuer Chat',
    openChat: 'Oeffnen',
    suggestions: ['Produktempfehlung: helfen Sie mir wählen', 'Wie ist der Status meiner Bestellung?', 'Habt ihr kleine Sticker auf Rolle?'],
    kbLabel: 'Wissensdatenbank',
    emailTitle: 'Bevor wir beginnen', emailHint: 'Geben Sie Ihre E-Mail-Adresse ein, damit wir den Chat wiederfinden und Sie bei Bedarf kontaktieren können.', emailPlaceholder: 'ihre@email.de', emailContinue: 'Chat starten', emailInvalid: 'Geben Sie eine gültige E-Mail-Adresse ein.',
    handoffButton: 'Ja, Frage an das Team weitergeben',
  },
  en: {
    welcome: 'Hi! I am the Printen.eu assistant. I can safely check order status, forward requests to support and help with products or file requirements.',
    title: 'Printen.eu assistant',
    subtitle: 'Order help and product advice',
    loading: 'Checking...',
    placeholder: 'Type your question...',
    fallback: 'Sorry, I could not create an answer. Please try again.',
    readMore: 'Read more about it',
    error: 'Sorry, something went wrong. Try again or email support@printen.eu.',
    open: 'Open chat',
    close: 'Close chat',
    send: 'Send message',
    history: 'Open previous chat',
    historyLoaded: 'Previous chat loaded.',
    noHistory: 'There is no previous chat for this session yet.',
    historyTitle: 'Previous chats',
    newChat: 'New chat',
    openChat: 'Open',
    suggestions: ['Product advice: help me choose', 'What is the status of my order?', 'Do you offer small stickers on a roll?'],
    kbLabel: 'Knowledge base',
    emailTitle: 'Before we start', emailHint: 'Enter your email address so we can find your conversation and contact you if needed.', emailPlaceholder: 'you@example.com', emailContinue: 'Start chat', emailInvalid: 'Enter a valid email address.',
    handoffButton: 'Yes, pass my question to the team',
  },
  fr: {
    welcome: 'Bonjour ! Je suis l’assistant Printen.eu. Je peux vérifier le statut de commande, transmettre les demandes au support et aider avec les produits ou fichiers.',
    title: 'Assistant Printen.eu',
    subtitle: 'Aide commande et conseil produit',
    loading: 'Verification...',
    placeholder: 'Tapez votre question...',
    fallback: 'Desole, je n ai pas pu creer de reponse. Veuillez reessayer.',
    readMore: 'En savoir plus',
    error: 'Désolé, une erreur est survenue. Réessayez ou écrivez à support@printen.eu.',
    open: 'Ouvrir le chat',
    close: 'Fermer le chat',
    send: 'Envoyer le message',
    history: 'Ouvrir le chat precedent',
    historyLoaded: 'Chat precedent charge.',
    noHistory: 'Aucun chat precedent pour cette session.',
    historyTitle: 'Chats precedents',
    newChat: 'Nouveau chat',
    openChat: 'Ouvrir',
    suggestions: ['Conseil produit : aidez-moi a choisir', 'Quel est le statut de ma commande ?', 'Avez-vous de petits stickers en rouleau ?'],
    kbLabel: 'Base de connaissances',
    emailTitle: 'Avant de commencer', emailHint: 'Saisissez votre adresse e-mail afin que nous puissions retrouver la conversation et vous contacter si nécessaire.', emailPlaceholder: 'vous@exemple.fr', emailContinue: 'Démarrer le chat', emailInvalid: 'Saisissez une adresse e-mail valide.',
    handoffButton: 'Oui, transmettre ma question à l’équipe',
  },
};

const SITE_LOCALES = ['nl', 'de', 'en', 'fr', 'da', 'it', 'es', 'pl', 'pt', 'cs'];

/**
 * De taal van de pagina. `<html>` heeft geen lang-attribuut (dat staat op de
 * div in de [locale]-layout), dus `document.documentElement.lang` was altijd
 * leeg en elke chat startte in het Nederlands, ook op /en/products/...
 * Het eerste padsegment is betrouwbaar; daarna het dichtstbijzijnde lang.
 */
function getPageLanguage(): string {
  if (typeof window === 'undefined') return '';
  const segment = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase() || '';
  if (SITE_LOCALES.includes(segment)) return segment;
  return (document.documentElement.lang || document.querySelector('[lang]')?.getAttribute('lang') || '').toLowerCase();
}

function toChatLocale(lang: string): Locale {
  if (lang.startsWith('nl')) return 'nl';
  if (lang.startsWith('de')) return 'de';
  if (lang.startsWith('en')) return 'en';
  if (lang.startsWith('fr')) return 'fr';
  // Een andere sitetaal (es, it, pl...): Engels ligt dichterbij dan Nederlands.
  return lang ? 'en' : 'nl';
}

function getDocumentLocale(): Locale {
  return toChatLocale(getPageLanguage());
}

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createSessionId() {
  return `pc_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function getStoredSessionId() {
  try {
    return window.localStorage.getItem('printen_chat_session_id');
  } catch {
    return null;
  }
}

function storeSessionId(sessionId: string) {
  try {
    window.localStorage.setItem('printen_chat_session_id', sessionId);
    rememberSessionId(sessionId);
  } catch {
    // Storage can be blocked in some mobile/privacy contexts.
  }
}

function getStoredSessionIds() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem('printen_chat_session_ids') || '[]');
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

function rememberSessionId(sessionId: string) {
  try {
    const next = [sessionId, ...getStoredSessionIds().filter((value) => value !== sessionId)].slice(0, 20);
    window.localStorage.setItem('printen_chat_session_ids', JSON.stringify(next));
  } catch {
    // Storage can be blocked in some mobile/privacy contexts.
  }
}

/**
 * Vetgedrukte stukken uit het modelantwoord echt vet tonen.
 *
 * Het model schrijft **Dibond** en de widget toonde de sterretjes letterlijk.
 * Meer markdown dan dit hoeft niet: het zijn korte chatzinnen.
 */
function renderBold(text: string) {
  const delen = text.split(/\*\*([^*]+)\*\*/g);
  if (delen.length === 1) return text;
  return delen.map((deel, i) => (i % 2 === 1 ? <strong key={i}>{deel}</strong> : deel));
}

function renderContent(content: string) {
  return content.split('\n').map((line, index) => {
    const trimmed = line.trim();
    const urlMatch = trimmed.match(/https?:\/\/[^\s]+|\/(?:nl\/producten|de\/produkte|en\/products|fr\/produits)\/[^\s]+/);

    return (
      <span key={`${line}-${index}`}>
        {urlMatch ? (
          <>
            {renderBold(line.slice(0, line.indexOf(urlMatch[0])))}
            <a
              href={urlMatch[0]}
              target={urlMatch[0].startsWith('http') ? '_blank' : undefined}
              rel={urlMatch[0].startsWith('http') ? 'noopener noreferrer' : undefined}
              className="font-semibold text-blue-700 underline underline-offset-2"
            >
              {urlMatch[0]}
            </a>
            {renderBold(line.slice(line.indexOf(urlMatch[0]) + urlMatch[0].length))}
          </>
        ) : (
          renderBold(line)
        )}
        {index < content.split('\n').length - 1 && <br />}
      </span>
    );
  });
}

/**
 * Kennisbankartikelen onder een antwoord.
 *
 * Wist de bot het niet, dan kreeg de bezoeker een doodlopend keuzemenu. Nu
 * staan er artikelen onder die wel over zijn vraag gaan. Weet de bot het wel,
 * dan is het het hele verhaal voor wie doorleest.
 */
function ArticleLinks({ articles, label }: { articles?: ArticleLink[]; label: string }) {
  if (!articles?.length) return null;

  return (
    <div className="mt-3">
      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="space-y-1.5">
        {articles.map((article) => (
          <a
            key={article.href}
            href={article.href}
            className="block rounded-lg border border-slate-200 bg-white p-2 text-left shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
          >
            <p className="text-sm font-bold leading-5 text-slate-950">{article.title}</p>
            <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-slate-600">{article.summary}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

function ProductCards({ products }: { products?: ProductCard[] }) {
  if (!products?.length) return null;

  return (
    <div className="mt-3 space-y-2">
      {products.map((product) => (
        <a
          key={product.id || product.href}
          href={product.href}
          className="group flex gap-3 rounded-lg border border-slate-200 bg-white p-2 text-left shadow-sm transition hover:border-rose-300 hover:bg-rose-50"
        >
          <div className="h-16 w-16 flex-none overflow-hidden rounded-md border border-slate-200 bg-slate-100">
            {product.image ? (
              <img src={product.image} alt="" className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs font-bold text-slate-400">
                EU
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="line-clamp-2 text-sm font-bold leading-5 text-slate-950 group-hover:text-rose-700">
                {product.name}
              </p>
              {product.price && (
                <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-700">
                  {product.price.replace('EUR ', '€')}
                </span>
              )}
            </div>
            {product.reason && (
              <p className="mt-1 line-clamp-2 text-xs leading-4 text-slate-600">{product.reason}</p>
            )}
            {product.features?.length ? (
              <p className="mt-1 line-clamp-1 text-[11px] font-medium text-slate-500">
                {product.features.slice(0, 2).join(' | ')}
              </p>
            ) : null}
          </div>
        </a>
      ))}
    </div>
  );
}

export function PrintenAssistant() {
  const [locale, setLocale] = useState<Locale>('nl');
  const copy = widgetCopy[locale];
  const [open, setOpen] = useState(false);
  // Groen bolletje alleen tijdens kantooruren; pas na mount bepalen zodat server en client gelijk renderen.
  const [officeOpen, setOfficeOpen] = useState(false);
  useEffect(() => {
    const update = () => setOfficeOpen(isOfficeOpen());
    update();
    const timer = window.setInterval(update, 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const supportRootRef = useRef<HTMLDivElement>(null);
  const [visibleViewport, setVisibleViewport] = useState<{ top: number; left: number; height: number; width: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const viewport = window.visualViewport;
    const updateViewport = () => setVisibleViewport({
      top: viewport?.offsetTop ?? 0,
      left: viewport?.offsetLeft ?? 0,
      height: viewport?.height ?? window.innerHeight,
      width: viewport?.width ?? window.innerWidth,
    });
    updateViewport();
    viewport?.addEventListener('resize', updateViewport);
    viewport?.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);
    return () => {
      viewport?.removeEventListener('resize', updateViewport);
      viewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
    };
  }, [open]);
  useEffect(() => {
    const unsubscribe = subscribeSupportRequests(() => setOpen(true));
    const root = supportRootRef.current;
    // Readiness describes the external subscription, not SSR/open state.
    root?.setAttribute('data-ready', 'true');
    return () => {
      root?.setAttribute('data-ready', 'false');
      unsubscribe();
    };
  }, []);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // Twilper streamt het antwoord; zolang er tekst binnenkomt geen laadbolletje.
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [statusText, setStatusText] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [chatEmail, setChatEmail] = useState(() => typeof window === 'undefined' ? '' : readOptionalBrowserValue(() => window.localStorage, 'printen_chat_email'));
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [state, setState] = useState<ChatState>(() => {
    if (typeof window === 'undefined') return {};
    const existing = getStoredSessionId();
    const sessionId = existing || createSessionId();
    if (!existing) storeSessionId(sessionId);
    if (existing) rememberSessionId(existing);
    // Na een volledige herlaad moet de aanwezigheidspoll doorlopen; bewaar het
    // Twilper-gesprek per tabblad, alleen geldig voor deze chatsessie.
    const [storedSession, storedTwilperId] = readOptionalBrowserValue(() => window.sessionStorage, TWILPER_PRESENCE_KEY).split('|');
    return existing && storedSession === existing && storedTwilperId
      ? { sessionId, twilperConversationId: storedTwilperId }
      : { sessionId };
  });
  useEffect(() => {
    if (state.sessionId && state.twilperConversationId) {
      writeOptionalBrowserValue(() => window.sessionStorage, TWILPER_PRESENCE_KEY, `${state.sessionId}|${state.twilperConversationId}`);
    }
  }, [state.sessionId, state.twilperConversationId]);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: copy.welcome,
    },
  ]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastLiveMessageAtRef = useRef('');

  useEffect(() => {
    const documentLocale = getDocumentLocale();
    setLocale(documentLocale);
    setMessages((current) =>
      current.length === 1 && current[0].id === 'welcome'
        ? [{ ...current[0], content: widgetCopy[documentLocale].welcome }]
        : current
    );
  }, []);

  useEffect(() => {
    if (open) {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, open, loading]);

  // Twilper toont aanwezigheid ("Online", "Bekijkt nu") alleen op basis van
  // deze polls. Daarom ook met dichte chat, zolang er een gesprek is: dan
  // elke 30 seconden, open elke 3. De pagina gaat als ?page= mee zodat de
  // aanvraag simpel blijft (geen CORS-preflight voor een eigen header).
  useEffect(() => {
    if (!state.twilperConversationId) return;

    const apiKey = process.env.NEXT_PUBLIC_TWILPERCHAT_API_KEY?.trim();
    const apiBase = (process.env.NEXT_PUBLIC_TWILPERCHAT_API_BASE?.trim() || 'https://api.twilper.com').replace(/\/$/, '');
    if (!apiKey) return;
    const liveApiKey = apiKey;

    let cancelled = false;

    async function pollLiveChat() {
      try {
        const params = new URLSearchParams({ api_key: liveApiKey });
        if (lastLiveMessageAtRef.current) params.set('since', lastLiveMessageAtRef.current);
        const page = `${window.location.origin}${window.location.pathname}`;
        if (page.length <= 2048) params.set('page', page);
        const response = await fetch(`${apiBase}/api/livechat/poll/${state.twilperConversationId}?${params.toString()}`);
        if (!response.ok) return;

        const data = await response.json();
        if (cancelled || !Array.isArray(data.messages) || data.messages.length === 0) return;

        setMessages((current) => {
          const next = [...current];
          for (const liveMessage of data.messages) {
            const content = String(liveMessage.content || '').trim();
            if (!content) continue;
            if (next.some((message) => message.role === 'assistant' && message.content === content)) continue;
            next.push({ id: createId(), role: 'assistant', content });
            if (liveMessage.created_at) lastLiveMessageAtRef.current = liveMessage.created_at;
          }
          return next;
        });
      } catch {
        // Livechat polling should never break the customer chat.
      }
    }

    const interval = window.setInterval(pollLiveChat, open ? 3000 : 30000);
    void pollLiveChat();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [open, state.twilperConversationId]);

  async function sendMessage(message: string, options: { action?: 'handoff' } = {}) {
    const cleanMessage = message.trim();
    if (!cleanMessage || loading) return;
    if (state.sessionId) rememberSessionId(state.sessionId);
    setHistoryOpen(false);

    const userMessage: Message = {
      id: createId(),
      role: 'user',
      content: cleanMessage,
    };

    setMessages((current) => [...current, userMessage]);
    setInput('');
    setLoading(true);
    setStatusText('');

    // Eén bubbel voor dit antwoord: eerst de gestreamde tekst, daarna precies
    // het eindantwoord (of de terugval) op dezelfde plek. Nooit twee antwoorden.
    const replyId = createId();
    const upsertReply = (reply: Message | null) => setMessages((current) => {
      if (!reply) return current.filter((item) => item.id !== replyId);
      return current.some((item) => item.id === replyId)
        ? current.map((item) => (item.id === replyId ? reply : item))
        : [...current, reply];
    });

    try {
      const response = await fetch('/api/chatbot/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson, application/json' },
        body: JSON.stringify({
          message: cleanMessage,
          state,
          sessionId: state.sessionId,
          twilperConversationId: state.twilperConversationId,
          pageUrl: window.location.href,
          pageLanguage: getPageLanguage(),
          userAgent: navigator.userAgent,
          customerEmail: chatEmail,
          stream: true,
          ...(options.action ? { action: options.action } : {}),
        }),
      });

      let data: ChatPayload | null = null;
      if ((response.headers.get('content-type') || '').includes('application/x-ndjson') && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamed = '';
        const handleLine = (line: string) => {
          if (!line.trim()) return;
          let event: ChatPayload;
          try {
            event = JSON.parse(line);
          } catch {
            return;
          }
          if (event.type === 'token' && typeof event.content === 'string') {
            streamed += event.content;
            setStatusText('');
            setStreamingId(replyId);
            upsertReply({ id: replyId, role: 'assistant', content: streamed });
          } else if (event.type === 'status' && typeof event.message === 'string') {
            setStatusText(event.message);
          } else if (event.type === 'final') {
            data = event;
          }
        };
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          lines.forEach(handleLine);
        }
        handleLine(buffer);
      } else {
        data = await response.json();
      }
      const final = data as ChatPayload | null;
      if (!final) throw new Error('Chat answer incomplete');

      setState(final.state || {});
      // De server antwoordt in de taal van de bezoeker; de knoppen en teksten
      // van de widget volgen mee.
      const replyLocale = final.state?.locale;
      if (replyLocale === 'nl' || replyLocale === 'de' || replyLocale === 'en' || replyLocale === 'fr') setLocale(replyLocale);
      // Tijdens een live-overname antwoordt de medewerker via de livechat-poll;
      // zonder eigen tekst tonen we dan geen (terugval)bericht van de bot.
      if (final.silent === true) {
        upsertReply(null);
        return;
      }
      upsertReply({
        id: replyId,
        role: 'assistant',
        content: final.reply || copy.fallback,
        products: Array.isArray(final.products) ? final.products : undefined,
        articles: Array.isArray(final.articles) ? final.articles : undefined,
        handoffOffer: final.handoffOffer === true,
      });
    } catch {
      upsertReply({
        id: replyId,
        role: 'assistant',
        content: copy.error,
      });
    } finally {
      setLoading(false);
      setStreamingId(null);
      setStatusText('');
    }
  }

  async function loadConversation(sessionId: string) {
    if (!sessionId || historyLoading) return;

    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/chatbot/history?sessionId=${encodeURIComponent(sessionId)}`);
      const data = await response.json();
      const storedMessages = Array.isArray(data.messages) ? data.messages : [];

      if (!storedMessages.length) {
        setMessages((current) => [
          ...current,
          { id: createId(), role: 'assistant', content: copy.noHistory },
        ]);
        return;
      }

      rememberSessionId(sessionId);
      storeSessionId(sessionId);
      setState((current) => ({
        ...current,
        sessionId,
        conversationId: data.conversation?.id || current.conversationId,
        twilperConversationId: data.conversation?.twilperConversationId || current.twilperConversationId,
        orderNumber: data.conversation?.orderNumber || current.orderNumber,
        verifiedOrderId: current.verifiedOrderId,
      }));
      setHistoryOpen(false);
      setMessages([
        ...storedMessages.map((message: { id?: string; role: Role; content: string; products?: ProductCard[] }) => ({
          id: message.id || createId(),
          role: message.role,
          content: message.content,
          products: Array.isArray(message.products) ? message.products : undefined,
        })),
        { id: createId(), role: 'assistant', content: copy.historyLoaded },
      ]);
    } catch {
      setMessages((current) => [
        ...current,
        { id: createId(), role: 'assistant', content: copy.error },
      ]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function loadHistoryList() {
    if (historyLoading) return;

    const sessionIds = Array.from(new Set([
      state.sessionId,
      ...getStoredSessionIds(),
    ].filter(Boolean))) as string[];

    if (!sessionIds.length) {
      setHistoryItems([]);
      setHistoryOpen(true);
      return;
    }

    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/chatbot/history?sessionIds=${encodeURIComponent(sessionIds.join(','))}`);
      const data = await response.json();
      setHistoryItems(Array.isArray(data.conversations) ? data.conversations : []);
      setHistoryOpen(true);
    } catch {
      setMessages((current) => [
        ...current,
        { id: createId(), role: 'assistant', content: copy.error },
      ]);
    } finally {
      setHistoryLoading(false);
    }
  }

  function startNewChat() {
    const sessionId = createSessionId();
    storeSessionId(sessionId);
    setState({ sessionId });
    setHistoryOpen(false);
    setInput('');
    setEmailConfirmed(false);
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: copy.welcome,
      },
    ]);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void sendMessage(input);
  }

  function confirmEmail(event: FormEvent) {
    event.preventDefault();
    const normalized = chatEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(normalized)) {
      setEmailError(copy.emailInvalid);
      return;
    }
    writeOptionalBrowserValue(() => window.localStorage, 'printen_chat_email', normalized);
    setChatEmail(normalized);
    setEmailError('');
    setEmailConfirmed(true);
  }

  async function checkFile(file: File | undefined) {
    if (!file || loading) return;
    setMessages((current) => [...current, { id: `file_${Date.now()}`, role: 'user', content: `[bestand] ${file.name}` }]);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locale', locale);
      const response = await fetch('/api/chatbot/file-check', { method: 'POST', body: formData });
      const data = await response.json();
      setMessages((current) => [...current, { id: `filecheck_${Date.now()}`, role: 'assistant', content: String(data.reply || copy.error || 'Er ging iets mis met de bestandscheck.') }]);
    } catch {
      setMessages((current) => [...current, { id: `filecheck_${Date.now()}`, role: 'assistant', content: 'De bestandscheck lukte even niet. Probeer het zo nog eens.' }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div ref={supportRootRef} id="printen-assistant" data-ready="false" data-open={open ? 'true' : 'false'} style={visibleViewport ? {
      '--assistant-visible-top': `${visibleViewport.top}px`,
      '--assistant-visible-left': `${visibleViewport.left}px`,
      '--assistant-visible-height': `${visibleViewport.height}px`,
      '--assistant-visible-width': `${visibleViewport.width}px`,
    } as CSSProperties : undefined} className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-[99995] font-sans print:hidden sm:bottom-5 sm:right-5">
      <style>{`@media (max-width: 1023px) {
        #printen-assistant-panel {
          position: fixed;
          top: calc(var(--assistant-visible-top, 0px) + 0.5rem + env(safe-area-inset-top));
          left: calc(var(--assistant-visible-left, 0px) + 0.75rem);
          width: calc(var(--assistant-visible-width, 100vw) - 1.5rem);
          height: calc(var(--assistant-visible-height, 100dvh) - 1rem - env(safe-area-inset-top) - env(safe-area-inset-bottom));
          max-height: none;
          margin: 0;
        }
      }`}</style>
      {open && (
        <section id="printen-assistant-panel" aria-label={copy.title} className="mb-4 flex min-h-0 h-[min(680px,calc(100dvh-120px))] w-[min(420px,calc(100vw-32px))] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
          <header className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-950 px-4 py-3 text-white">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-rose-600">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold">{copy.title}</p>
                <p className="text-xs text-slate-300">{copy.subtitle}</p>
                {officeOpen && (
                  <a href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`} className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-300 hover:text-emerald-200">
                    <Phone className="h-3 w-3" />
                    {locale === 'nl' ? `Bel ons: ${companyInfo.contact.phoneDisplay}` : locale === 'de' ? `Anrufen: ${companyInfo.contact.phone}` : locale === 'fr' ? `Appelez-nous : ${companyInfo.contact.phone}` : `Call us: ${companyInfo.contact.phone}`}
                  </a>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="h-11 w-11 text-white hover:bg-white/10 hover:text-white"
                onClick={loadHistoryList}
                disabled={historyLoading}
                aria-label={copy.history}
                title={copy.history}
              >
                {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="h-11 w-11 text-white hover:bg-white/10 hover:text-white"
                onClick={() => setOpen(false)}
                aria-label={copy.close}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </header>

          {!emailConfirmed && (
            <form onSubmit={confirmEmail} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-slate-50 p-6 lg:justify-center">
              <div>
                <h3 className="text-lg font-bold text-slate-950">{copy.emailTitle}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{copy.emailHint}</p>
              </div>
              <Input
                type="email"
                value={chatEmail}
                onChange={(event) => { setChatEmail(event.target.value); setEmailError(''); }}
                placeholder={copy.emailPlaceholder}
                autoComplete="email"
                required
                autoFocus
              />
              {emailError && <p className="text-sm font-medium text-red-600">{emailError}</p>}
              <Button type="submit" className="bg-rose-600 text-white hover:bg-rose-700">{copy.emailContinue}</Button>
            </form>
          )}

          <div className={cn("min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4", !emailConfirmed && "hidden")}>
            {historyOpen ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-bold text-slate-950">{copy.historyTitle}</h3>
                  <button
                    type="button"
                    className="rounded-md bg-slate-950 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-slate-800"
                    onClick={startNewChat}
                  >
                    {copy.newChat}
                  </button>
                </div>
                {historyItems.length === 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
                    {copy.noHistory}
                  </div>
                ) : (
                  historyItems.map((item) => (
                    <button
                      key={item.id || item.sessionId}
                      type="button"
                      className="block w-full rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:border-rose-300 hover:bg-rose-50"
                      onClick={() => loadConversation(item.sessionId)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-bold text-slate-950">
                          {item.preview ? item.preview.slice(0, 42) : copy.historyTitle}
                        </span>
                        <span className="whitespace-nowrap text-xs font-semibold text-rose-700">{copy.openChat}</span>
                      </div>
                      {item.lastMessageAt && (
                        <p className="mt-1 text-xs text-slate-500">
                          {new Date(item.lastMessageAt).toLocaleString(locale)}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            ) : messages.map((message) => (
              <div
                key={message.id}
                className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[86%] rounded-lg px-3 py-2 text-sm leading-6 shadow-sm',
                    message.products?.length && 'max-w-[96%]',
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'border border-slate-200 bg-white text-slate-900'
                  )}
                >
                  {renderContent(message.content)}
                  {message.role === 'assistant' && <ProductCards products={message.products} />}
                  {message.role === 'assistant' && <ArticleLinks articles={message.articles} label={copy.readMore} />}
                  {message.role === 'assistant' && message.handoffOffer && message.id === messages[messages.length - 1]?.id && (
                    <button
                      type="button"
                      className="mt-3 rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-800 transition hover:bg-rose-100"
                      onClick={() => sendMessage(copy.handoffButton, { action: 'handoff' })}
                      disabled={loading}
                    >
                      {copy.handoffButton}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && !streamingId && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {statusText || copy.loading}
                </div>
              </div>
            )}
            <div ref={scrollRef} />
          </div>

          <div className={cn("border-t border-slate-200 bg-white p-3", !emailConfirmed && "hidden")}>
            <div className="mb-3 flex flex-wrap gap-2">
              {/* Zelf lezen in plaats van vragen: dezelfde artikelen die de
                  bot voeden, als gewone pagina. */}
              <a
                href={kennisbankPad[locale] || kennisbankPad.nl}
                className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800 transition hover:border-blue-400 hover:bg-blue-100"
              >
                <BookOpen className="h-3.5 w-3.5" />
                {copy.kbLabel}
              </a>
              {copy.suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-rose-300 hover:text-rose-700"
                  onClick={() => sendMessage(suggestion)}
                  disabled={loading}
                >
                  {suggestion}
                </button>
              ))}
            </div>
            <form onSubmit={onSubmit} className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void sendMessage(input);
                  }
                }}
                placeholder={copy.placeholder}
                className="min-h-11 resize-none text-slate-950"
                rows={1}
              />
              <label className="flex h-10 w-10 flex-none cursor-pointer items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-blue-400 hover:text-blue-700" aria-label="Bestand checken" title="Check of je bestand scherp genoeg is">
                <Paperclip className="h-4 w-4" />
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(event) => {
                    void checkFile(event.target.files?.[0] || undefined);
                    event.target.value = '';
                  }}
                />
              </label>
              <Button type="submit" size="icon" disabled={loading || !input.trim()} aria-label={copy.send}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </section>
      )}

      {!open && (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="relative h-12 w-12 rounded-full bg-rose-600 p-0 text-white shadow-lg hover:bg-rose-700"
          aria-label={copy.open}
          title={officeOpen ? (locale === 'nl' ? 'Chat, we zijn nu bereikbaar' : locale === 'de' ? 'Chat, wir sind jetzt erreichbar' : locale === 'fr' ? 'Chat, nous sommes disponibles' : 'Chat, we are available now') : copy.open}
        >
          <MessageCircle className="h-5 w-5" />
          {officeOpen && <span aria-hidden className="absolute right-0.5 top-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />}
        </Button>
      )}
    </div>
  );
}
